import { getLocalStorageItem, setLocalStorageItem } from '@snapchat/mw-common';
import noop from 'lodash-es/noop';
import { useCallback, useContext, useEffect, useState } from 'react';

import { AppContext } from '../../AppContext';
import { Config } from '../../config';
import { ElementLocation } from '../../context/ElementLocationContext/ElementLocationContext';
import { logEvent, SubscribedEventType } from '../../helpers/logging';
import { UserAction } from '../../types/events';
import { ShopifyContext } from '../ShopifyProvider';
import type { CartEvent, CartLine, CartState, PersistedCart } from './types';

const shopifyCartKey = 'mwp-shopify-cart';

export const initialCartState: CartState = {
  lines: [],
  cartOpen: false,
  addLine: noop,
  removeLine: noop,
  setCartOpen: noop,
  discountCode: '',
  setDiscountCode: noop,
};

// TODO: Discount code flow is for the waitlist purchase flow (months away).
// This must remain purely client-side to avoid CDN cache interference.
// Needs validation logic added later — currently we pass the code to Shopify
// checkout without any client-side validation.
const getUrlDiscount = (urlString: string) => {
  try {
    const url = new URL(urlString);
    return url.searchParams.get('discount') ?? '';
  } catch {
    return '';
  }
};

/**
 * Validate that persisted cart data matches the CartLine[] shape.
 * If invalid (e.g. old `Record<string, number>` format), returns null
 * so the caller can clear and start fresh.
 */
const validatePersistedCart = (data: unknown): CartLine[] | null => {
  if (!data || typeof data !== 'object') return null;

  const parsed = data as Record<string, unknown>;

  // Old format: { lineItems: { "gid://...": 1 } } — clear it
  if ('lineItems' in parsed && !('lines' in parsed)) return null;

  if (!Array.isArray(parsed.lines)) return null;

  const lines = parsed.lines as unknown[];

  for (const line of lines) {
    if (!line || typeof line !== 'object') return null;

    const entry = line as Record<string, unknown>;

    if (typeof entry.optionId !== 'string' || typeof entry.productKey !== 'string') return null;
    if (typeof entry.quantity !== 'number' || entry.quantity < 1) return null;
    if (entry.sellingPlanId !== undefined && typeof entry.sellingPlanId !== 'string') return null;
  }

  return parsed.lines as CartLine[];
};

/** Persist only the lines array to localStorage. */
const persistCart = (lines: CartLine[]) => {
  const data: PersistedCart = { lines };
  setLocalStorageItem(shopifyCartKey, JSON.stringify(data));
};

/** Find a line by optionId, or return -1. */
const findLineIndex = (lines: CartLine[], optionId: string): number =>
  lines.findIndex(line => line.optionId === optionId);

export const useCart = (): CartState => {
  const appContext = useContext(AppContext);
  const { elementLocation } = useContext(ElementLocation);
  const { catalog, productOptionById } = useContext(ShopifyContext);
  const country = appContext.userLocation.country;

  // Seed discount code from URL query param (e.g. ?discount=SAVE10)
  const urlDiscount = getUrlDiscount(appContext.getCurrentUrl());

  const [lines, setLines] = useState<CartLine[]>([]);
  const [cartOpen, setCartOpenState] = useState(false);
  const [discountCode, setDiscountCode] = useState(urlDiscount);

  const setCartOpen = (open: boolean) => {
    logEvent({
      eventCategory: 'Cart',
      eventAction: UserAction.Click,
      eventLabel: open ? 'Open Cart' : 'Close Cart',
      subscribedEventType: SubscribedEventType.USER_INTERACTION,
    });

    setCartOpenState(open);
  };

  const addLine = useCallback(
    ({ productKey, optionId, currencyCode }: CartEvent) => {
      const product = catalog[productKey];
      const option = productOptionById[optionId]?.option;

      if (!product) return;

      const idx = findLineIndex(lines, optionId);
      let newLines: CartLine[];

      if (idx >= 0) {
        newLines = lines.map((line, index) =>
          index === idx ? { ...line, quantity: line.quantity + 1 } : line
        );
      } else {
        const currencyEntry = product.sellingPlan?.depositData?.[currencyCode];

        newLines = [
          ...lines,
          { optionId, productKey, quantity: 1, sellingPlanId: currencyEntry?.sellingPlanId },
        ];
      }

      persistCart(newLines);
      setLines(newLines);

      logEvent({
        eventCategory: 'Cart',
        eventAction: 'Add',
        eventLabel: optionId,
        numberOfProducts: 1,
        ecommerce: {
          currencyCode,
          add: {
            products: [
              {
                name: product.title,
                id: option?.sku ?? product.options[0]?.sku ?? '',
                variant: option?.title ?? product.title,
                price: (option?.price ?? product.options[0]?.price ?? 0).toString(),
                shopifyid: optionId,
                quantity: 1,
              },
            ],
          },
        },
        subscribedEventType: SubscribedEventType.ECOMMERCE,
        context: { elementLocation },
      });

      setCartOpenState(true);
    },
    [catalog, productOptionById, elementLocation, lines]
  );

  const removeLine = useCallback(
    ({ productKey, optionId, currencyCode }: CartEvent) => {
      const product = catalog[productKey];
      const option = productOptionById[optionId]?.option;

      if (!product) return;

      const idx = findLineIndex(lines, optionId);

      if (idx < 0) return;

      const existing = lines[idx]!;
      let newLines: CartLine[];

      if (existing.quantity <= 1) {
        newLines = lines.filter((_, index) => index !== idx);
      } else {
        newLines = lines.map((line, index) =>
          index === idx ? { ...line, quantity: line.quantity - 1 } : line
        );
      }

      persistCart(newLines);
      setLines(newLines);

      logEvent({
        eventCategory: 'Cart',
        eventAction: 'Remove',
        eventLabel: optionId,
        currencyCode,
        productIds: optionId,
        ecommerce: {
          remove: {
            products: [
              {
                name: product.title,
                id: option?.sku ?? product.options[0]?.sku ?? '',
                variant: option?.title ?? product.title,
                price: (option?.price ?? product.options[0]?.price ?? 0).toString(),
                shopifyid: optionId,
                quantity: 1,
              },
            ],
          },
        },
        subscribedEventType: SubscribedEventType.ECOMMERCE,
        context: { elementLocation },
      });
    },
    [catalog, productOptionById, elementLocation, lines]
  );

  const cartInit = useCallback(() => {
    const raw = getLocalStorageItem(shopifyCartKey);

    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        const validLines = validatePersistedCart(parsed);

        if (validLines) {
          setLines(validLines);
          return;
        }
      } catch {
        // Invalid JSON — fall through to clear
      }

      // Old format or invalid data — clear and start fresh
      persistCart([]);
    }
  }, []);

  useEffect(() => {
    if (!Config.shopify) return;

    cartInit();
  }, [country, cartInit]);

  return {
    lines,
    cartOpen,
    addLine,
    removeLine,
    setCartOpen,
    discountCode,
    setDiscountCode,
  };
};
